import React from 'react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {Banner} from '@primer/react/experimental'
import {ShieldLockIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useLocation, type Location} from '@github-ui/react-router'
import {deleteCookie, getCookie, setCookie} from '@github-ui/cookies'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useSso} from '@github-ui/use-sso'
import styles from './GlobalSSOBanner.module.css'
import {SlideDismiss} from './SlideDismiss'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'

const MAX_DISPLAYED_ORGS = 3
const COOKIE_EXPIRATION_DAYS = 30
export const COOKIE_NAME = 'dismissed_sso_orgs'

export function storeDismissedOrgIds(ssoOrgs: GlobalSsoBannerProps['ssoOrgs']) {
  const orgIds = ssoOrgs.map(org => org.id).sort()
  const oneMonth = COOKIE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000 // expiration days in milliseconds
  const expiration = new Date(Date.now() + oneMonth).toUTCString()
  deleteCookie(COOKIE_NAME) // clear the old cookie before setting a new one
  setCookie(COOKIE_NAME, JSON.stringify(orgIds), expiration)
}

/** Gets all the orgs from the last time the banner was dismissed via the stored cookie */
function getDismissedOrgIdsFromCookie(): string[] {
  const cookie = getCookie(COOKIE_NAME)

  if (cookie?.value) {
    try {
      const orgIds = JSON.parse(cookie.value)
      return Array.isArray(orgIds) ? orgIds : []
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (_error: unknown) {
      // If parsing fails, we assume the cookie is invalid and delete it
      deleteCookie(COOKIE_NAME)
      return []
    }
  }

  return []
}

/**
 * Check if the SSO banner info stored as a cookie matches what is currently tried to be displayed.
 * If it does, the banner will be suppressed.
 */
function shouldCookieSuppressBanner(ssoOrgs: GlobalSsoBannerProps['ssoOrgs']): boolean {
  const dismissedOrgIds = getDismissedOrgIdsFromCookie()
  return ssoOrgs.length === dismissedOrgIds.length && ssoOrgs.every(org => dismissedOrgIds.includes(org.id))
}

export interface GlobalSsoBannerProps {
  ssoOrgs: Array<{
    id: string
    name: string
    login: string
    avatar_url?: string
  }>
}

export function isSSOOrgInfo(ssoOrg: unknown): ssoOrg is GlobalSsoBannerProps['ssoOrgs'][number] {
  return typeof ssoOrg === 'object' && ssoOrg !== null && 'id' in ssoOrg && 'name' in ssoOrg && 'login' in ssoOrg
}

export function generateSSOUrl(id: string, location: Location): string {
  const baseUrl = `/orgs/${encodeURIComponent(id)}/sso`
  const returnTo = location.pathname + location.search + location.hash
  return `${baseUrl}?return_to=${encodeURIComponent(returnTo)}`
}

export interface SSOActionMenuProps {
  ssoOrgs: GlobalSsoBannerProps['ssoOrgs']
  redirectLocation: Location
}

function SSOActionMenu({ssoOrgs, redirectLocation}: SSOActionMenuProps) {
  if (ssoOrgs.length === 0) {
    return null
  }

  if (ssoOrgs.length === 1) {
    const org = ssoOrgs[0]

    if (org === undefined) {
      return null
    }

    return (
      <Button as={'a'} href={generateSSOUrl(org.login, redirectLocation)}>
        Single sign-on
      </Button>
    )
  }

  return (
    <ActionMenu>
      <ActionMenu.Button size="small">Single sign-on</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          {ssoOrgs.map(org => (
            <ActionList.LinkItem key={org.login} href={generateSSOUrl(org.login, redirectLocation)}>
              {org.avatar_url && (
                <ActionList.LeadingVisual>
                  <GitHubAvatar size={24} src={org.avatar_url} />
                </ActionList.LeadingVisual>
              )}
              {org.name}
            </ActionList.LinkItem>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function GlobalSsoBanner({ssoOrgs}: GlobalSsoBannerProps) {
  const location = useLocation()
  const [bannerState, setBannerState] = React.useState<'visible' | 'hidden'>('visible')

  if (ssoOrgs.length === 0) {
    return null
  }

  // Hide the banner when the user clicks the close button
  const handleDismiss = () => {
    setBannerState('hidden')
    // Write cookie with the org IDs that were present when dismissed
    storeDismissedOrgIds(ssoOrgs)
  }

  const renderDescription = () => {
    if (ssoOrgs.length === 0) return 'Single sign-on to see results'

    const displayOrgs = ssoOrgs.slice(0, MAX_DISPLAYED_ORGS)
    const remainingCount = ssoOrgs.length - MAX_DISPLAYED_ORGS
    const organizationText = ssoOrgs.length === 1 ? 'organization' : 'organizations'

    const formatOrgList = () => {
      const formattedOrgNames = displayOrgs.map(org => <strong key={org.login}>{org.name}</strong>)

      if (formattedOrgNames.length === 1) {
        return formattedOrgNames[0]
      } else if (formattedOrgNames.length === 2) {
        return (
          <>
            {formattedOrgNames[0]} and {formattedOrgNames[1]}
          </>
        )
      } else {
        // MAX_DISPLAYED_ORGS orgs, with potential overflow
        const lastIndex = formattedOrgNames.length - 1
        const baseList = (
          <>
            {formattedOrgNames[0]}, {formattedOrgNames[1]}, and {formattedOrgNames[lastIndex]}
          </>
        )
        return remainingCount > 0 ? (
          <>
            {baseList}, and {remainingCount} more
          </>
        ) : (
          baseList
        )
      }
    }

    return (
      <>
        Single sign-on to see results in the {formatOrgList()} {organizationText}
      </>
    )
  }

  return (
    <SlideDismiss hidden={bannerState !== 'visible'}>
      <Banner
        title="Single-sign on"
        className={styles.banner}
        hideTitle
        icon={<ShieldLockIcon />}
        description={renderDescription()}
        onDismiss={handleDismiss}
        primaryAction={<SSOActionMenu ssoOrgs={ssoOrgs} redirectLocation={location} />}
        data-testid="global-sso-banner"
      />
    </SlideDismiss>
  )
}

type GlobalSSOBannerAppPayload = {
  current_sso_orgs_match_dismissed_cookie?: boolean
}

function useGlobalBannerSSO() {
  const {ssoOrgs, baseAvatarUrl} = useSso()
  const {current_sso_orgs_match_dismissed_cookie} = useAppPayload<GlobalSSOBannerAppPayload>() ?? {}

  const ssoOrgsWithAvatars = React.useMemo(
    () =>
      ssoOrgs.filter(isSSOOrgInfo).map(org => ({
        ...org,
        avatar_url: `${baseAvatarUrl}/u/${org.id}`,
      })),
    [ssoOrgs, baseAvatarUrl],
  )

  return {
    suppressBanner: current_sso_orgs_match_dismissed_cookie === true || shouldCookieSuppressBanner(ssoOrgsWithAvatars),
    ssoOrgs: ssoOrgsWithAvatars,
    baseAvatarUrl,
  }
}

/** Entry point for the GlobalSSOBanner when used in React pages */
export function ContextualGlobalSsoBanner() {
  const {ssoOrgs, suppressBanner} = useGlobalBannerSSO()

  if (ssoOrgs.length === 0 || !isFeatureEnabled('global_sso_banner') || suppressBanner) {
    return null
  }

  return <GlobalSsoBanner ssoOrgs={ssoOrgs} />
}

try{ SSOActionMenu.displayName ||= 'SSOActionMenu' } catch {}
try{ GlobalSsoBanner.displayName ||= 'GlobalSsoBanner' } catch {}
try{ ContextualGlobalSsoBanner.displayName ||= 'ContextualGlobalSsoBanner' } catch {}